import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogIn, Mail, ArrowLeft, CheckCircle, Building2 } from "lucide-react";
import api from "@/lib/api";

const LOGO_COLOR = "/logo-horizontal.png";

export default function LoginPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("choose"); // choose, email, sent
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/crm';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/auth/magic-link", { email: email.trim().toLowerCase() });
      setMode("sent");
    } catch (err) {
      setError(err.response?.data?.detail || "Errore durante la richiesta");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-white flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#20c997] via-[#0f292f] to-[#0f292f]" />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.15\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <img src={LOGO_COLOR} alt="PX Group" className="h-10 w-auto self-start brightness-0 invert" />
          <div>
            <h1 className="text-4xl font-bold text-white leading-tight mb-4">Il tuo CRM immobiliare, semplice e potente.</h1>
            <p className="text-white/60 max-w-md text-lg">Gestisci operazioni, pipeline, business plan, vendite e documenti in un unico strumento.</p>
            <div className="flex gap-4 mt-8">
              {["Pipeline", "Cash Flow", "IRR", "Drive", "AI"].map(t => (
                <span key={t} className="text-[10px] uppercase tracking-wider text-[#20c997] font-semibold bg-white/5 px-3 py-1.5 rounded-full border border-white/10">{t}</span>
              ))}
            </div>
          </div>
          <p className="text-white/30 text-xs">PX Group S.R.L.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-sm w-full">
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-[#0f292f] mb-2">Accedi al CRM</h2>
            <p className="text-sm text-[#666]">Solo gli utenti invitati possono accedere.</p>
          </div>

          {mode === "choose" && (
            <div className="space-y-3">
              <button onClick={handleGoogleLogin} className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-200 hover:border-[#20c997] text-[#0f292f] font-medium py-4 px-6 rounded-lg transition-all duration-300 hover:shadow-md" data-testid="google-login-btn">
                <svg width="20" height="20" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                Accedi con Google
              </button>

              <div className="flex items-center gap-3 py-2">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-[#999]">oppure</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              <button onClick={() => setMode("email")} className="w-full flex items-center justify-center gap-3 bg-[#0f292f] hover:bg-[#0f292f]/90 text-white font-medium py-4 px-6 rounded-lg transition-all duration-300" data-testid="email-login-btn">
                <Mail size={20} />
                Accedi con link via email
              </button>
            </div>
          )}

          {mode === "email" && (
            <form onSubmit={handleMagicLink} className="space-y-4">
              <div>
                <label className="text-sm text-[#555] block mb-2">Inserisci la tua email aziendale</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="nome@azienda.it" className="w-full border-2 border-gray-200 focus:border-[#20c997] rounded-lg py-3 px-4 text-[#0f292f] outline-none transition-colors" data-testid="magic-email-input" />
              </div>
              {error && <p className="text-sm text-red-500" data-testid="magic-error">{error}</p>}
              <button type="submit" disabled={loading} className="w-full bg-[#20c997] hover:bg-[#1bb583] text-white font-medium py-3.5 rounded-lg transition-all flex items-center justify-center gap-2" data-testid="magic-submit-btn">
                {loading ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Mail size={18} />}
                {loading ? "Invio in corso..." : "Invia link di accesso"}
              </button>
              <button type="button" onClick={() => { setMode("choose"); setError(""); }} className="w-full text-sm text-[#999] hover:text-[#0f292f] transition-colors flex items-center justify-center gap-1">
                <ArrowLeft size={14} /> Torna indietro
              </button>
            </form>
          )}

          {mode === "sent" && (
            <div className="text-center" data-testid="magic-sent">
              <div className="w-16 h-16 rounded-full bg-[#20c997]/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-[#20c997]" />
              </div>
              <h3 className="text-lg font-bold text-[#0f292f] mb-2">Link inviato!</h3>
              <p className="text-sm text-[#666] mb-6">Se la tua email e autorizzata, riceverai un link di accesso. Controlla la tua casella di posta o chiedi all'amministratore.</p>
              <button onClick={() => { setMode("choose"); setEmail(""); }} className="text-sm text-[#20c997] hover:underline">Prova un altro metodo</button>
            </div>
          )}

          <div className="mt-8 text-center">
            <button onClick={() => navigate("/")} className="text-sm text-[#999] hover:text-[#20c997] transition-colors" data-testid="back-to-site-btn">Torna al sito</button>
          </div>
        </div>
      </div>
    </div>
  );
}
